import { useDescope } from '@descope/react-sdk';
import {
	HolidayVillage,
	Search,
	SwitchAccountOutlined
} from '@mui/icons-material';
import {
	Box,
	Button,
	CircularProgress,
	InputAdornment,
	Stack,
	TextField,
	Typography,
	useTheme
} from '@mui/material';
import { useNavigateToFirstWidget } from 'app/navigation';
import { SSO_REQUIRED_ERR_CODE } from 'consts';
import React, { useCallback, useMemo, useState } from 'react';
import { useLoggedInUser, useTenant } from 'state/user';

const SEARCH_THRESHOLD = 10;

const TenantSelectorView: React.FC = () => {
	const { user } = useLoggedInUser();
	const { tenant: activeTenant } = useTenant();
	const { selectTenant } = useDescope();
	const [tenantId, setTenantId] = useState<string | null>(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const muiTheme = useTheme();
	const navigateToFirstWidget = useNavigateToFirstWidget();

	const handleSelectTenant = useCallback(async () => {
		/* istanbul ignore next: blocked by disabled button  */
		if (!tenantId) {
			return;
		}

		try {
			setLoading(true);
			setError(null);

			const res = await selectTenant(tenantId);
			if (!res.ok) {
				const errorKey =
					res.error?.errorCode === SSO_REQUIRED_ERR_CODE
						? 'tenantSelector.errorSsoRequired'
						: 'tenantSelector.error';
				setError(intl.tr(errorKey));
			} else {
				navigateToFirstWidget();
			}
		} catch {
			/* istanbul ignore next  */
			setError(intl.tr('tenantSelector.error'));
		} finally {
			setLoading(false);
		}
	}, [navigateToFirstWidget, selectTenant, tenantId]);

	// Default the highlight to the active tenant and block confirming it, so
	// reopening the picker shows the current tenant selected and Select only
	// enables for a different one.
	const activeTenantId = activeTenant?.tenantId ?? null;
	const selectedTenantId = tenantId ?? activeTenantId;

	const isConfirmDisabled = useMemo(
		() =>
			!selectedTenantId ||
			loading ||
			!!error ||
			selectedTenantId === activeTenantId,
		[activeTenantId, error, loading, selectedTenantId]
	);

	const showSearch =
		user?.userTenants?.length && user?.userTenants.length > SEARCH_THRESHOLD;

	const filteredTenants = useMemo(
		() =>
			(showSearch
				? user?.userTenants?.filter((t) =>
						t.tenantName.toLowerCase().includes(searchQuery.toLowerCase())
					)
				: user?.userTenants) || [],
		[searchQuery, showSearch, user?.userTenants]
	);

	if (!user?.userTenants?.length) {
		return (
			<Stack
				data-testid="tenant-selector-view"
				direction="column"
				alignItems="center"
				justifyContent="center"
				height="100%"
				gap={2}
			>
				<SwitchAccountOutlined
					sx={{
						fontSize: 48,
						color: muiTheme.palette.action.disabled
					}}
				/>
				<Typography variant="h5">{intl.tr('tenantSelector.title')}</Typography>
				<Typography variant="body1" color="text.secondary">
					{intl.tr('tenantSelector.subtitle')}
				</Typography>
				<Typography variant="body2" color="error">
					{intl.tr('tenantSelector.noOrganizations')}
				</Typography>
			</Stack>
		);
	}

	return (
		<Stack
			data-testid="tenant-selector-view"
			direction="column"
			alignItems="center"
			justifyContent="center"
			height="100%"
			gap={3}
		>
			<Stack direction="column" alignItems="center" gap={1}>
				<HolidayVillage
					sx={{
						fontSize: 45,
						color: muiTheme.palette.primary.main
					}}
				/>
				<Typography variant="h5">{intl.tr('tenantSelector.title')}</Typography>
				<Typography variant="body1" color="text.secondary">
					{intl.tr('tenantSelector.subtitle')}
				</Typography>
			</Stack>

			<Stack direction="column" gap={2} sx={{ maxWidth: 500, width: '100%' }}>
				{showSearch && (
					<TextField
						data-testid="tenant-selector-search"
						size="small"
						fullWidth
						placeholder={intl.tr('tenantSelector.searchPlaceholder')}
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Search fontSize="small" />
								</InputAdornment>
							)
						}}
					/>
				)}

				<Box sx={{ overflow: 'auto', height: 'min(400px, 40vh)' }}>
					<Stack direction="column" gap={1}>
						{filteredTenants.map((tenant) => {
							const isSelected = selectedTenantId === tenant.tenantId;

							return (
								<Button
									key={tenant.tenantId}
									fullWidth
									variant={isSelected ? 'contained' : 'outlined'}
									onClick={() => {
										setTenantId(tenant.tenantId);
										setError(null);
									}}
									data-testid={`tenant-selector-button-${tenant.tenantId}`}
									sx={{
										justifyContent: 'flex-start',
										padding: 2,
										textAlign: 'left'
									}}
								>
									<Typography variant="body1">{tenant.tenantName}</Typography>
								</Button>
							);
						})}
					</Stack>
				</Box>
				<Button
					data-testid="tenant-selector-confirm"
					variant="contained"
					color="primary"
					sx={{ height: 38, justifySelf: 'center', alignSelf: 'center' }}
					disabled={isConfirmDisabled}
					onClick={handleSelectTenant}
				>
					{loading ? (
						<CircularProgress size={20} color="inherit" />
					) : (
						intl.tr('tenantSelector.selectButton')
					)}
				</Button>

				{error && (
					<Typography
						data-testid="tenant-selector-error"
						variant="body2"
						color="error"
						align="center"
					>
						{error}
					</Typography>
				)}
			</Stack>
		</Stack>
	);
};

export default TenantSelectorView;
